import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import './LandingPage.css';
import './GeneratorTimova.css';
import { useSeo } from '../hooks/useSeo';
import FaqItem from './FaqItem';
import PublicPageNav from './PublicPageNav';


const TEAM_COLORS = [
  { bg: 'rgba(16, 185, 129, 0.12)', border: '#10b981', label: 'Tim 1' },
  { bg: 'rgba(56, 189, 248, 0.12)', border: '#38bdf8', label: 'Tim 2' },
  { bg: 'rgba(251, 191, 36, 0.12)', border: '#fbbf24', label: 'Tim 3' },
  { bg: 'rgba(244, 114, 182, 0.12)', border: '#f472b6', label: 'Tim 4' },
  { bg: 'rgba(167, 139, 250, 0.12)', border: '#a78bfa', label: 'Tim 5' },
  { bg: 'rgba(248, 113, 113, 0.12)', border: '#f87171', label: 'Tim 6' },
];

const parseNames = (text) =>
  text
    .split(/\r?\n|,|;/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const splitIntoTeams = (names, teamCount) => {
  const shuffled = shuffle(names);
  const teams = Array.from({ length: teamCount }, () => []);
  shuffled.forEach((name, i) => {
    teams[i % teamCount].push(name);
  });
  return teams;
};

const GeneratorTimova = () => {
  useSeo({
    title: 'Generator timova online - nasumična podjela igrača | StatArena',
    description:
      'Besplatni generator timova online. Unesi imena igrača i aplikacija nasumično podijeli sudionike u 2, 3, 4 ili više balansiranih timova. Idealno za nogomet, školu, team building, radionice i zabavu.',
    canonical: 'https://statarena.app/generator-timova',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/generator-timova',
    ogTitle: 'Generator timova - nasumična podjela igrača u timove',
    ogDescription:
      'Besplatni online alat za random podjelu igrača u timove. Bez registracije, bez reklama, radi na mobitelu.',
  });

  const [namesText, setNamesText] = useState('');
  const [teamCount, setTeamCount] = useState(2);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef(null);

  const names = useMemo(() => parseNames(namesText), [namesText]);

  const handleGenerate = () => {
    setCopied(false);
    if (names.length < 2) {
      setError('Unesi barem 2 imena (svako u novi red).');
      setTeams([]);
      return;
    }
    if (teamCount > names.length) {
      setError(`Imaš ${names.length} igrača, ne mogu napraviti ${teamCount} timova.`);
      setTeams([]);
      return;
    }
    setError('');
    const generated = splitIntoTeams(names, teamCount);
    setTeams(generated);
    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleReroll = () => {
    setCopied(false);
    setTeams(splitIntoTeams(names, teamCount));
  };

  const handleClear = () => {
    setNamesText('');
    setTeams([]);
    setError('');
    setCopied(false);
  };

  const handleCopy = async () => {
    if (teams.length === 0) return;
    const text = teams
      .map((team, i) => `${TEAM_COLORS[i]?.label || `Tim ${i + 1}`}:\n${team.join('\n')}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Kopiranje nije uspjelo:', err);
    }
  };

  const handleExample = () => {
    setNamesText(
      [
        'Ivan',
        'Marko',
        'Luka',
        'Petar',
        'Tomislav',
        'Ante',
        'Josip',
        'Mate',
        'Filip',
        'Domagoj',
        'Nikola',
        'Stipe',
      ].join('\n')
    );
    setError('');
  };

  return (
    <div className="landing">
      <PublicPageNav />

      <section className="landing-hero gt-hero">
        <span className="hero-eyebrow">Besplatno · Bez registracije · Radi na mobitelu</span>
        <h1 className="landing-hero-title">
          Generator timova - <span className="accent">nasumična podjela</span> u par klikova
        </h1>
        <p className="landing-hero-subtitle">
          Unesi imena sudionika, odaberi broj timova i aplikacija će random
          podijeliti igrače u balansirane grupe. Idealno za rekreativni nogomet,
          školske sportove, team building radionice, kvizove, rođendane i sve
          ostale situacije gdje trebaš brzu i pravednu podjelu na timove - bez
          Excela, bez papirića, bez rasprave tko ide s kim.
        </p>
      </section>

      <section className="gt-tool-section">
        <div className="gt-tool">
          <div className="gt-tool-grid">
            <div className="gt-input-col">
              <label htmlFor="gt-names" className="gt-label">
                Imena igrača <span className="gt-label-hint">(svako u novi red ili odvojeno zarezom)</span>
              </label>
              <textarea
                id="gt-names"
                className="gt-textarea"
                placeholder={'Ivan\nMarko\nLuka\nPetar\n...'}
                value={namesText}
                onChange={(e) => setNamesText(e.target.value)}
                rows={10}
              />
              <div className="gt-counter">
                <span>{names.length} {names.length === 1 ? 'igrač' : 'igrača'}</span>
                <button type="button" className="gt-link-btn" onClick={handleExample}>
                  Ubaci primjer
                </button>
                {namesText && (
                  <button type="button" className="gt-link-btn" onClick={handleClear}>
                    Obriši sve
                  </button>
                )}
              </div>
            </div>

            <div className="gt-controls-col">
              <label className="gt-label">Broj timova</label>
              <div className="gt-team-picker">
                {[2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`gt-team-btn ${teamCount === n ? 'active' : ''}`}
                    onClick={() => setTeamCount(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>

              <div className="gt-summary">
                <div className="gt-summary-row">
                  <span>Igrača po timu:</span>
                  <strong>
                    {names.length >= teamCount
                      ? `~${Math.floor(names.length / teamCount)}${
                          names.length % teamCount === 0 ? '' : `-${Math.ceil(names.length / teamCount)}`
                        }`
                      : '—'}
                  </strong>
                </div>
                <div className="gt-summary-row">
                  <span>Ukupno timova:</span>
                  <strong>{teamCount}</strong>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-primary btn-lg gt-generate-btn"
                onClick={handleGenerate}
              >
                Promiješaj i podijeli
              </button>

              {error && <div className="gt-error">{error}</div>}
            </div>
          </div>
        </div>

        {teams.length > 0 && (
          <div className="gt-results" ref={resultsRef}>
            <div className="gt-results-header">
              <h2>Podjela timova</h2>
              <div className="gt-results-actions">
                <button type="button" className="btn btn-secondary" onClick={handleReroll}>
                  <span role="img" aria-label="Ponovi">🔄</span> Promiješaj ponovo
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCopy}>
                  <span role="img" aria-label="Kopiraj">📋</span> {copied ? 'Kopirano!' : 'Kopiraj'}
                </button>
              </div>
            </div>

            <div className="gt-teams-grid">
              {teams.map((team, i) => {
                const color = TEAM_COLORS[i] || TEAM_COLORS[0];
                return (
                  <div
                    key={i}
                    className="gt-team-card"
                    style={{ background: color.bg, borderColor: color.border }}
                  >
                    <h3 style={{ color: color.border }}>
                      {color.label} <span className="gt-team-count">({team.length})</span>
                    </h3>
                    <ol className="gt-team-list">
                      {team.map((name, j) => (
                        <li key={j}>{name}</li>
                      ))}
                    </ol>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="landing-section">
        <div className="section-header landing-section-header">
          <h2>Za što sve možeš koristiti generator timova</h2>
          <p className="landing-section-sub">
            Nasumična podjela u timove nije samo za sport - koristan je svuda
            gdje više ljudi treba brzo i pravedno razvrstati u grupe.
          </p>
        </div>

        <div className="landing-features">
          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Nogomet">⚽</span></div>
            <h3>Rekreativni nogomet i mali sportovi</h3>
            <p>
              Mali nogomet, futsal, košarka na pola terena, odbojka na pijesku -
              brza random podjela prije utakmice, bez sukoba i beskonačnog
              biranja kapetana.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Škola">🎒</span></div>
            <h3>Škola i vrtić - podjela razreda u grupe</h3>
            <p>
              Učitelji i odgajatelji: podijeli razred u radne grupe za projekt,
              natjecanje ili igru. Svaki put drugačija kombinacija, bez
              favoriziranja.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Posao">💼</span></div>
            <h3>Team building i firmaške radionice</h3>
            <p>
              Organiziraš team building? Podijeli kolege u timove za escape
              room, kviz ili radionicu - nitko ne može reći da je slagano
              namjerno.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Kviz">🧠</span></div>
            <h3>Kvizovi i pub quiz večeri</h3>
            <p>
              Organizator kviza? Ubaci sve prijavljene, odaberi broj ekipa i
              rezultat je gotov - savršeno za nepoznate sudionike koji se
              miješaju u ekipe.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Balon">🎈</span></div>
            <h3>Rođendani i dječji tulumi</h3>
            <p>
              Potjere, štafete, potraga za blagom - svaka igra u timovima
              počinje podjelom. Generator to riješi dok roditelji postavljaju
              tortu.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Kartice">🃏</span></div>
            <h3>Društvene igre i turniri</h3>
            <p>
              Tabu, Activity, Pictionary, pikado turnir - svaka igra u kojoj
              treba balansirano podijeliti prijatelje u ekipe da bi bilo
              zanimljivo.
            </p>
          </article>
        </div>
      </section>

      <section className="landing-section landing-section-alt">
        <div className="section-header landing-section-header">
          <h2>Kako radi generator timova</h2>
          <p className="landing-section-sub">
            Bez registracije, bez aplikacije za preuzimanje, bez skrivenih
            troškova. Od unosa do rezultata - manje od 30 sekundi.
          </p>
        </div>

        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num">1</div>
            <h3>Unesi imena</h3>
            <p>Upiši ili zalijepi imena igrača - svako u novi red ili odvojeno zarezom.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">2</div>
            <h3>Odaberi broj timova</h3>
            <p>Klikni 2, 3, 4, 5 ili 6 - koliko god timova trebaš za svoju aktivnost.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">3</div>
            <h3>Promiješaj</h3>
            <p>Algoritam nasumično razvrsta igrače u timove podjednakih veličina.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">4</div>
            <h3>Podijeli rezultat</h3>
            <p>Kopiraj podjelu jednim klikom i pošalji ekipi u WhatsApp ili Viber.</p>
          </div>
        </div>
      </section>

      <section id="faq" className="landing-section">
        <div className="section-header landing-section-header">
          <h2>Često postavljena pitanja o generatoru timova</h2>
          <p className="landing-section-sub">
            Sve što trebaš znati prije nego što prvi put napraviš nasumičnu
            podjelu sudionika u timove preko ovog alata.
          </p>
        </div>

        <div className="faq-list">
          <FaqItem question="Je li generator timova stvarno besplatan?">
            <p>
              Da, generator timova na StatArena platformi potpuno je besplatan
              za svakoga. Ne tražimo registraciju, ne prikazujemo reklame i
              nema nikakvog ograničenja na broj podjela. Koristi koliko god
              puta trebaš za svoj trening, školski razred, team building,
              pub kviz ili rođendanski tulum.
            </p>
          </FaqItem>

          <FaqItem question="Koliko igrača mogu unijeti u generator?">
            <p>
              Praktički neograničeno - alat radi odlično i za 4 osobe i za
              100 imena. Možeš unositi po jedno ime u red ili zalijepiti listu
              odvojenu zarezima i točka-zarezima. Najviše se koristi za
              podjele od 6 do 30 sudionika, što pokriva prosječnu nogometnu
              ekipu, razred ili firmaški team building.
            </p>
          </FaqItem>

          <FaqItem question="Koliko timova mogu napraviti odjednom?">
            <p>
              Generator podržava podjelu od 2 do 6 timova. Ako trebaš dvije
              ekipe za mali nogomet, četiri grupe za kviz ili šest timova za
              školsku štafetu - jedan klik i podjela je gotova. Raspodjela je
              ravnomjerna, pa su timovi podjednake veličine (razlika nikad
              veća od jednog igrača).
            </p>
          </FaqItem>

          <FaqItem question="Je li podjela stvarno nasumična?">
            <p>
              Da. Koristimo Fisher-Yates shuffle algoritam koji daje pravu
              nasumičnu permutaciju sudionika prije raspodjele u timove.
              Svaki klik na "Promiješaj ponovo" generira novu random kombinaciju,
              tako da možeš pogurati do one podjele koja svima odgovara ako
              se baš neka činila neuravnotežena.
            </p>
          </FaqItem>

          <FaqItem question="Pamti li alat moje igrače i prošle podjele?">
            <p>
              Ne, ovaj generator je bez registracije i ništa se ne sprema na
              server - sve radi u tvom pregledniku. Ako organiziraš
              rekreativni nogomet s istom ekipom iz tjedna u tjedan i želiš
              pratiti statistiku, rezultate i rang listu, preporučamo da
              otvoriš besplatan StatArena račun - aplikacija je dizajnirana
              upravo za to.
            </p>
          </FaqItem>

          <FaqItem question="Radi li generator na mobitelu?">
            <p>
              Da, alat je potpuno responzivan i radi bez problema na Android
              i iOS pametnim telefonima, tabletima i stolnim računalima u
              svakom modernom pregledniku. Možeš ga dodati i kao prečac na
              početni zaslon pa funkcionira kao aplikacija - bez instalacije
              iz Trgovine Play ili App Storea.
            </p>
          </FaqItem>

          <FaqItem question="Radi li podjela balansirane timove po jačini?">
            <p>
              Ovaj javni generator radi čisto nasumičnu podjelu, bez obzira
              na vještinu igrača. Ako organiziraš rekreativni nogomet i želiš
              da algoritam balansira timove prema statistici pojedinih igrača
              (golovi, asistencije, pobjede, MVP ocjene), to radi naša glavna
              StatArena aplikacija - automatski uzima stvarne podatke iz
              prošlih termina i slaže balansirane ekipe.
            </p>
          </FaqItem>

          <FaqItem question="Mogu li podijeliti rezultat ekipi?">
            <p>
              Naravno. Nakon podjele klikni "Kopiraj" i dobit ćeš tekst
              spreman za lijepljenje u WhatsApp, Viber, Messenger, e-mail ili
              gdje god komunicirate s ekipom. Svaki tim ima svoju boju i
              listu igrača pa je rezultat pregledan i bez dodatnog
              formatiranja.
            </p>
          </FaqItem>
        </div>
      </section>

      <section className="landing-cta">
        <h2>Organiziraš rekreativni nogomet s istom ekipom?</h2>
        <p>
          Ako ista ekipa igra svaki tjedan, StatArena ti može napraviti puno
          više od nasumične podjele - balansirane timove po statistici igrača,
          raspored termina, MVP rang listu i arhivu cijele sezone. Sve
          besplatno.
        </p>
        <div className="landing-cta-actions">
          <Link to="/register" className="btn btn-primary btn-lg">
            Registriraj svoju ekipu besplatno
          </Link>
          <Link to="/" className="btn btn-secondary btn-lg">
            Saznaj više o StatAreni
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <span className="landing-logo-mark" role="img" aria-label="Nogometna lopta">⚽</span>
            <span className="landing-logo-text">StatArena</span>
            <span className="landing-footer-tag">Aplikacija za rekreativni nogomet</span>
          </div>
          <div className="landing-footer-links">
            <Link to="/">Početna</Link>
            <Link to="/generator-timova">Generator timova</Link>
            <Link to="/raspored-turnira">Raspored turnira</Link>
            <Link to="/semafor-online">Online semafor</Link>
            <Link to="/login">Prijava</Link>
            <Link to="/register">Registracija</Link>
          </div>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} StatArena · statarena.app
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GeneratorTimova;

